import EnvConfigHelper from '../../helpers/envConfigHelper';

export interface TranscriptionSessionConfig {
    type: "transcription";
    audio: {
        input: {
            format: {
                type: "audio/pcm";
                rate: number;
            };
            noise_reduction: {
                type: "near_field" | "far_field";
            };
            transcription: {
                language?: string;
                prompt?: string;
            };
        };
    };
}

export interface TranscriptionSessionResponse {
    token: string;
    base_url: string;
}

export type SpeechState = "started" | "stopped";

export interface RealtimeTranscriberOptions {
    language?: string;
    backendEndpoint?: string;
    disableUserMediaErrorAlert?: boolean;
    prompt?: string;
}

/**
 * RealtimeTranscriber
 *
 * Handles real-time audio transcription using Azure OpenAI's WebRTC API.
 * Streams microphone audio and receives transcription deltas/completions.
 */
export class RealtimeTranscriber {
    private pc: RTCPeerConnection | null = null;
    private stream: MediaStream | null = null;
    private dataChannel: RTCDataChannel | null = null;
    private audioContext: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private levelBuffer: Uint8Array<ArrayBuffer> | null = null;
    private levelRafId: number | null = null;
    private lastLevel: number = 0;
    private smoothLevel: number = 0;
    private getAccessToken: () => Promise<string | undefined>;

    // Listeners
    private deltaListeners = new Set<(delta: string) => void>();
    private transcriptListeners = new Set<(transcript: string) => void>();
    private speechStateListeners = new Set<(state: SpeechState) => void>();
    private levelListeners = new Set<(level: number) => void>();
    private errorListeners = new Set<(error: Error) => void>();

    // Options
    private options: Required<Omit<RealtimeTranscriberOptions, 'prompt'>> & Pick<RealtimeTranscriberOptions, 'prompt'>; //prompt is left optional just in case as its existence, even as an empty variable, changes the behavior of the transcription model.

    constructor(
        getAccessToken: () => Promise<string | undefined>,
        options: RealtimeTranscriberOptions = {}
    ) {
        this.getAccessToken = getAccessToken;

        const baseURL = EnvConfigHelper.get('api-base-url', '');
        this.options = {
            language: options.language ?? "en",
            backendEndpoint:
                options.backendEndpoint ?? `${baseURL}/api/me/chat/realtime/transcription/create-session`,
            disableUserMediaErrorAlert: options.disableUserMediaErrorAlert ?? false,
            prompt: options.prompt ?? "You are a transcription model. Your task is to transcribe audio for AIDA, the Danfoss AI assistant. If there is no clear speech, output nothing.",
        };
    }

    /**
     * Start the transcription session.
     */
    async start(): Promise<void> {
        try {
            try {
                this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            }
            catch (error) {
                if (!this.options.disableUserMediaErrorAlert)
                    showGetUserMediaAlert(error);
                throw error;
            }

            const sessionResponse = await this.createSession();

            this.pc = new RTCPeerConnection();

            const audioTrack = this.stream.getAudioTracks()[0];
            this.pc.addTrack(audioTrack, this.stream);

            this.dataChannel = this.pc.createDataChannel("oai-events");
            this.setupDataChannelListeners();

            const offer = await this.pc.createOffer();
            await this.pc.setLocalDescription(offer);

            const answerSdp = await this.sendSdpOffer(
                sessionResponse.base_url,
                sessionResponse.token,
                offer.sdp!
            );

            await this.pc.setRemoteDescription({
                type: "answer",
                sdp: answerSdp,
            });

            this.startLevelMeter();

            //console.log("🎤 Realtime transcription connected");
        } catch (error) {

            console.error("❌ Realtime transcription error:", error);
            this.emitError(error instanceof Error ? error : new Error(String(error)));
            throw error;
        }
    }

    /**
     * Stop the transcription session.
     */
    async stop(): Promise<void> {
        try {
            this.stopLevelMeter();

            if (this.dataChannel) {
                this.dataChannel.close();
                this.dataChannel = null;
            }

            if (this.pc) {
                this.pc.close();
                this.pc = null;
            }

            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop());
                this.stream = null;
            }

            //console.log("🎤 Realtime transcription stopped");
        } catch (error) {
            console.error("❌ Realtime transcription stop error:", error);
        }
    }

    private async createSession(): Promise<TranscriptionSessionResponse> {
        const sessionConfig: TranscriptionSessionConfig = {
            type: "transcription",
            audio: {
                input: {
                    format: {
                        type: "audio/pcm",
                        rate: 24000,
                    },
                    noise_reduction: {
                        type: "near_field",
                    },
                    transcription: {
                        ...(this.options.language === "auto" ? {} : { language: this.options.language }),
                        prompt: this.options.prompt,
                    },
                },
            },
        };

        const token = await this.getAccessToken();

        const response = await fetch(this.options.backendEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(sessionConfig),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
                `Failed to create session: ${response.status} - ${errorText}`
            );
        }

        return response.json();
    }

    /**
     * Send SDP offer to Azure OpenAI and get the answer.
     */
    private async sendSdpOffer(
        baseUrl: string,
        token: string,
        sdp: string
    ): Promise<string> {
        const url = `${baseUrl.replace(/\/$/, "")}/openai/v1/realtime/calls`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/sdp",
            },
            body: sdp,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(
                `SDP negotiation failed: ${response.status} - ${errorText}`
            );
        }

        return response.text();
    }

    /**
     * Set up data channel event listeners.
     */
    private setupDataChannelListeners(): void {
        if (!this.dataChannel) return;

        this.dataChannel.onopen = () => {
            //console.log("📡 Data channel opened");
        };

        this.dataChannel.onclose = () => {
            //console.log("📡 Data channel closed");
        };

        this.dataChannel.onerror = (event) => {
            console.error("📡 Data channel error:", event);
        };

        this.dataChannel.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this.handleServerEvent(message);
            } catch (error) {
                console.error("Failed to parse server event:", error);
            }
        };
    }

    /**
     * Handle incoming server events.
     */
    private handleServerEvent(event: {
        type: string;
        [key: string]: unknown;
    }): void {
        //console.log("📨 Server event:", event.type);

        switch (event.type) {
            case "conversation.item.input_audio_transcription.delta":
                // Partial transcription
                const delta = event.delta as string;
                //console.log("[partial]", delta);
                this.deltaListeners.forEach((listener) => listener(delta));
                break;

            case "conversation.item.input_audio_transcription.completed":
                // Final transcription
                const transcript = event.transcript as string;
                //console.log("[final]", transcript);
                this.transcriptListeners.forEach((listener) => listener(transcript));
                break;

            case "input_audio_buffer.speech_started":
                this.speechStateListeners.forEach((listener) => listener("started"));
                break;

            case "input_audio_buffer.speech_stopped":
                this.speechStateListeners.forEach((listener) => listener("stopped"));
                break;

            case "error":
                const errorMessage =
                    (event.error as { message?: string; })?.message ?? "Unknown error";
                console.error("❌ Server error:", errorMessage);
                this.emitError(new Error(errorMessage));
                break;
        }
    }

    // ============ Audio Level Meter ============

    private startLevelMeter(): void {
        if (!this.stream) return;

        this.audioContext = new AudioContext();
        const source = this.audioContext.createMediaStreamSource(this.stream);

        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.analyser.smoothingTimeConstant = 0.8;

        source.connect(this.analyser);
        this.levelBuffer = new Uint8Array(this.analyser.fftSize) as Uint8Array<ArrayBuffer>;

        this.levelLoop();
    }

    private levelLoop = (): void => {
        this.levelRafId = requestAnimationFrame(this.levelLoop);

        if (!this.analyser || !this.levelBuffer) return;

        this.analyser.getByteTimeDomainData(this.levelBuffer);

        // Calculate RMS
        let sum = 0;
        for (let i = 0; i < this.levelBuffer.length; i++) {
            const sample = (this.levelBuffer[i] - 128) / 128;
            sum += sample * sample;
        }
        const rms = Math.sqrt(sum / this.levelBuffer.length);
        const level = Math.min(1, rms * 10);

        // Smooth the level
        const smoothing = 0.15;
        this.smoothLevel = this.smoothLevel * (1 - smoothing) + level * smoothing;

        // Only emit if changed significantly
        if (Math.abs(this.smoothLevel - this.lastLevel) >= 0.01) {
            this.lastLevel = this.smoothLevel;
            this.levelListeners.forEach((listener) => listener(this.smoothLevel));
        }
    };

    private stopLevelMeter(): void {
        if (this.levelRafId !== null) {
            cancelAnimationFrame(this.levelRafId);
            this.levelRafId = null;
        }

        if (this.audioContext && this.audioContext.state !== "closed") {
            this.audioContext.close();
            this.audioContext = null;
        }

        this.analyser = null;
        this.levelBuffer = null;
    }

    // ============ Event Listeners ============

    onDelta(listener: (delta: string) => void): () => void {
        this.deltaListeners.add(listener);
        return () => this.deltaListeners.delete(listener);
    }

    offDelta(listener: (delta: string) => void): void {
        this.deltaListeners.delete(listener);
    }

    onTranscript(listener: (transcript: string) => void): () => void {
        this.transcriptListeners.add(listener);
        return () => this.transcriptListeners.delete(listener);
    }

    offTranscript(listener: (transcript: string) => void): void {
        this.transcriptListeners.delete(listener);
    }

    onSpeechState(listener: (state: SpeechState) => void): () => void {
        this.speechStateListeners.add(listener);
        return () => this.speechStateListeners.delete(listener);
    }

    offSpeechState(listener: (state: SpeechState) => void): void {
        this.speechStateListeners.delete(listener);
    }

    onLevel(listener: (level: number) => void): () => void {
        this.levelListeners.add(listener);
        return () => this.levelListeners.delete(listener);
    }

    offLevel(listener: (level: number) => void): void {
        this.levelListeners.delete(listener);
    }

    onError(listener: (error: Error) => void): () => void {
        this.errorListeners.add(listener);
        return () => this.errorListeners.delete(listener);
    }

    offError(listener: (error: Error) => void): void {
        this.errorListeners.delete(listener);
    }

    private emitError(error: Error): void {
        this.errorListeners.forEach((listener) => listener(error));
    }
}

const showGetUserMediaAlert = (err: unknown) => {

    if (!(err instanceof DOMException)) {
        return;
    }

    let title = "Microphone access issue";
    let message =
        "We couldn't access your microphone. Please try again or check your browser settings.";

    switch (err.name) {
        case "NotAllowedError":
        case "PermissionDeniedError":
            title = "Microphone access blocked";
            message =
                "You chose not to allow microphone access. To continue, please enable the microphone in your browser's permission settings and try again.";
            break;

        case "NotFoundError":
        case "DevicesNotFoundError":
            title = "No microphone found";
            message =
                "We couldn't find a microphone on your device. Please connect one and try again.";
            break;

        case "NotReadableError":
        case "TrackStartError":
            title = "Microphone in use";
            message =
                "Your microphone is currently being used by another application. Please close other apps using the microphone and try again.";
            break;

        case "OverconstrainedError":
        case "ConstraintNotSatisfiedError":
            title = "Microphone not compatible";
            message =
                "Your microphone doesn't support the required settings. Try using a different microphone or adjusting your audio settings.";
            break;

        case "SecurityError":
            title = "Microphone access not allowed";
            message =
                "Microphone access is blocked because this page is not running in a secure context. Please use HTTPS and try again.";
            break;
    }
    alert(`${title}\n\n${message}`);
};